import { API_BASE_URL, IS_TEST_ENV } from '../config';

interface Lease {
  stable: string;
  delivered: string;
  expiresAt: number | null;
  retries: number;
  issuedAt: number;
}
const leases = new WeakMap<HTMLMediaElement, Lease>();
const deliveryPauses = new WeakSet<HTMLMediaElement>();
const deliveryTransitions = new WeakMap<
  HTMLMediaElement,
  { delivered: string; position: number }
>();

/** Keeps app-owned progress displays steady while a refreshed source restores its position. */
export function audioPlaybackPosition(audio: HTMLMediaElement): number {
  return deliveryTransitions.get(audio)?.position ?? audio.currentTime;
}

/** Native controls emit a pause when load() replaces their resource, even without user intent. */
export function consumeDeliveryPause(audio: HTMLMediaElement): boolean {
  const expected = deliveryPauses.has(audio);
  deliveryPauses.delete(audio);
  return expected;
}
const pending = new WeakMap<HTMLMediaElement, { stable: string; request: Promise<void> }>();

export function stableAudioSource(audio: HTMLMediaElement): string {
  const lease = leases.get(audio);
  return lease?.delivered === audio.src ? lease.stable : audio.src;
}

function isManagedUrl(source: string): boolean {
  try {
    const url = new URL(source, window.location.href);
    const api = new URL(API_BASE_URL || '/api', window.location.href);
    return (
      url.origin === api.origin &&
      /^\/api\/(?:media\/audio\/|public\/media\/audio\/share\/)/.test(url.pathname) &&
      !url.searchParams.has('download') &&
      !/\/(?:export|stems\.zip)$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

export function needsAudioRefresh(
  expiresAt: number | null,
  duration: number,
  position: number,
  now = Date.now()
): boolean {
  if (expiresAt === null) return false;
  const remaining = Number.isFinite(duration) ? Math.max(0, duration - position) : 0;
  return expiresAt - now <= (remaining + 60) * 1000;
}

/** Only contiguous buffered audio proves that playback needs no further network reads. */
export function hasBufferedRemainder(audio: HTMLMediaElement): boolean {
  const duration = audio.duration;
  if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(audio.currentTime))
    return false;
  // play() on an ended element restarts at zero, so the whole track must be buffered.
  const position = audio.ended || audio.currentTime >= duration ? 0 : audio.currentTime;
  if (position < 0) return false;
  for (let index = 0; index < audio.buffered.length; index++) {
    if (audio.buffered.start(index) <= position && audio.buffered.end(index) >= duration)
      return true;
  }
  return false;
}

/** Leases live only in memory, never in persisted song records or telemetry. */
export async function prepareAudioPlayback(audio: HTMLMediaElement, force = false): Promise<void> {
  if (IS_TEST_ENV && !API_BASE_URL) return;
  const stable = stableAudioSource(audio);
  if (!isManagedUrl(stable)) return;
  if (!force && !audio.error && hasBufferedRemainder(audio)) return;
  const existing = pending.get(audio);
  if (existing?.stable === stable) {
    return existing.request.catch(() => {
      throw new Error('Audio delivery is temporarily unavailable.');
    });
  }
  const lease = leases.get(audio);
  if (
    !force &&
    !audio.error &&
    lease?.stable === stable &&
    lease.delivered === audio.src &&
    (Date.now() - lease.issuedAt < 5000 ||
      !needsAudioRefresh(lease.expiresAt, audio.duration, audio.currentTime))
  )
    return;
  const originalSource = audio.src;
  const request = (async () => {
    const endpoint = new URL(stable, window.location.href);
    endpoint.searchParams.set('delivery', 'json');
    const response = await fetch(endpoint, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) throw new Error(`Playback authorization failed (${response.status}).`);
    const result = (await response.json()) as { url: string; expiresAt: string | null };
    const delivered = new URL(result.url, endpoint).href;
    const expiresAt = result.expiresAt === null ? null : Date.parse(result.expiresAt);
    if (expiresAt !== null && !Number.isFinite(expiresAt)) throw new Error('Invalid audio expiry.');
    // A track/version switch wins over an older signing response.
    if (audio.src !== originalSource) return;
    // The old request may have finished buffering while we were obtaining a lease.
    if (!force && !audio.error && hasBufferedRemainder(audio)) return;
    const position = audio.currentTime;
    leases.set(audio, {
      stable,
      delivered,
      expiresAt,
      retries: force ? 1 : 0,
      issuedAt: Date.now(),
    });
    if (audio.src === delivered && !force && !audio.error) return;
    if (!audio.paused) deliveryPauses.add(audio);
    const transition = { delivered, position };
    deliveryTransitions.set(audio, transition);
    audio.src = delivered;
    const restore = () => {
      if (deliveryTransitions.get(audio) !== transition) return;
      if (audio.src === delivered) audio.currentTime = position;
      deliveryTransitions.delete(audio);
    };
    audio.addEventListener('loadedmetadata', restore, { once: true });
    audio.load();
  })();
  pending.set(audio, { stable, request });
  try {
    await request;
  } catch {
    // Response parsing/network errors can contain provider URLs. Surface only a fixed message.
    throw new Error('Audio delivery is temporarily unavailable.');
  } finally {
    if (pending.get(audio)?.request === request) pending.delete(audio);
  }
}

export async function playDeliveredAudio(
  audio: HTMLMediaElement,
  shouldPlay: () => boolean = () => true
): Promise<void> {
  const stable = stableAudioSource(audio);
  if ((IS_TEST_ENV && !API_BASE_URL) || !isManagedUrl(stable)) {
    if (shouldPlay()) return audio.play();
    return;
  }
  await prepareAudioPlayback(audio);
  if (stableAudioSource(audio) === stable && shouldPlay()) await audio.play();
}

/** One refresh per delivery, including browsers that surface expiry only as a media error. */
export function attachAudioRecovery(
  audio: HTMLMediaElement,
  shouldPlay: () => boolean,
  failed: () => void
): () => void {
  let unleasedRecoverySource: string | null = null;
  const recover = () => {
    console.info('[audio-playback]', { event: 'media_failure', code: audio.error?.code ?? null });
    const lease = leases.get(audio);
    const stable = stableAudioSource(audio);
    if (
      !isManagedUrl(stable) ||
      (lease?.delivered === audio.src
        ? lease.expiresAt === null || lease.retries >= 1
        : unleasedRecoverySource === stable)
    ) {
      failed();
      return;
    }
    if (lease?.delivered === audio.src) lease.retries = 1;
    else unleasedRecoverySource = stable;
    console.info('[audio-playback]', { event: 'signed_url_refresh', result: 'attempt' });
    void prepareAudioPlayback(audio, true)
      .then(async () => {
        if (stableAudioSource(audio) === stable && shouldPlay()) await audio.play();
        console.info('[audio-playback]', { event: 'signed_url_refresh', result: 'success' });
      })
      .catch(() => {
        console.info('[audio-playback]', { event: 'signed_url_refresh', result: 'failed' });
        failed();
      });
  };
  const refreshForSeek = () => {
    const stable = stableAudioSource(audio);
    if (!isManagedUrl(stable) || audio.error || hasBufferedRemainder(audio)) return;
    const lease = leases.get(audio);
    if (
      lease?.delivered === audio.src &&
      (Date.now() - lease.issuedAt < 5000 ||
        !needsAudioRefresh(lease.expiresAt, audio.duration, audio.currentTime))
    )
      return;
    void playDeliveredAudio(audio, shouldPlay).catch(() => {
      if (stableAudioSource(audio) === stable) failed();
    });
  };
  audio.addEventListener('error', recover);
  audio.addEventListener('seeking', refreshForSeek);
  return () => {
    audio.removeEventListener('error', recover);
    audio.removeEventListener('seeking', refreshForSeek);
  };
}
